/**
 * Treatment Service - Handles all treatment-related operations in Firestore
 *
 * This is the canonical data layer for treatments. The legacy entry points in
 * `database/firestore.js` and `database/planerFirestore.js` delegate here so
 * every write goes through the same schema and cache invalidation.
 */

import { firebase, db } from '../firebase';
import { handleFirestoreError } from '../utils';
import {
	createCacheKey,
	fetchWithCache,
	invalidateCache,
	DEFAULT_CACHE_TTL_MS,
} from '../../../utils/firestoreCache';

const CACHE_NAMESPACES = {
	TREATMENTS: "dbTreatments",
};

const DEFAULT_DB_CACHE_MS = DEFAULT_CACHE_TTL_MS;

const resolveCacheArgs = (input) => {
	if (typeof input === "boolean") {
		return { forceRefresh: input, cacheDurationMs: undefined };
	}

	if (input && typeof input === "object") {
		const { forceRefresh = false, cacheDurationMs } = input;
		return {
			forceRefresh: Boolean(forceRefresh),
			cacheDurationMs:
				typeof cacheDurationMs === "number" && !Number.isNaN(cacheDurationMs)
					? cacheDurationMs
					: undefined,
		};
	}

	return { forceRefresh: false, cacheDurationMs: undefined };
};

const invalidateTreatmentCache = (userId) => {
	if (!userId) {
		return;
	}

	// Must be built with createCacheKey — a hand-built `${ns}:${id}` string
	// never matches the JSON-stringified key and silently leaves 60s of
	// stale data after every write.
	invalidateCache(createCacheKey(CACHE_NAMESPACES.TREATMENTS, userId));
};

/**
 * Gets all treatments for a user
 * @param {string} userID - The unique identifier for the user
 * @param {object} options - Cache options
 * @param {boolean} options.forceRefresh - Whether to bypass cache
 * @param {number} options.cacheDurationMs - Custom cache duration
 * @returns {Promise<Array>} Array of treatment objects
 */
export async function getTreatments(userID, options = {}) {
	const { forceRefresh, cacheDurationMs } = resolveCacheArgs(options);
	const cacheKey = createCacheKey(
		CACHE_NAMESPACES.TREATMENTS,
		userID
	);

	return fetchWithCache(
		cacheKey,
		async () => {
			try {
				const snapshot = await db
					.collection("Users")
					.doc(userID)
					.collection("Treatments")
					.where("Archived", "==", false)
					.get();

				return snapshot.docs.map((doc) => doc.data());
			} catch (error) {
				throw error;
			}
		},
		{
			ttlMs: cacheDurationMs ?? DEFAULT_DB_CACHE_MS,
			forceRefresh,
		}
	);
}

/**
 * The only fields a treatment *plan* edit is allowed to change.
 *
 * Everything outside this list is owned by other flows and must never be
 * written from the edit path:
 * - `t` is the document ID — writing it from form state creates a second
 *   document and orphans the original.
 * - `TreatmentCount`, `PhoneHistory` and `LastTreatmentDate` are the recorded
 *   dose ledger, written transactionally by recordTreatmentApplication and by
 *   the native mobile app. A form snapshot taken before a dose was logged
 *   would silently erase it.
 * - `Done`, `DoneAt`, `Archived` and `Validated` are lifecycle flags with
 *   their own dedicated service functions.
 */
export const EDITABLE_TREATMENT_FIELDS = [
	"ID",
	"Mac",
	"CustomName",
	"Group",
	"Diagnose",
	"Medicine",
	"Dosis",
	"Comment",
	"TreatmentAmount",
	"TreatmentInterval",
	"StartDate",
	"StartDateSeconds",
];

const isUsableDocumentId = (t) => {
	if (t === null || t === undefined || t === "") return false;
	if (typeof t === "number") return Number.isFinite(t);
	if (typeof t === "string") return t.trim() !== "";
	return false;
};

/**
 * Updates the editable plan fields of an existing treatment.
 *
 * This is the only supported write path for the treatment edit dialog. The
 * whitelist is enforced here rather than in the UI so no future caller can
 * regress into clobbering the dose ledger, and `update()` is used instead of
 * `set(..., { merge: true })` so a wrong/stale document ID fails loudly
 * instead of resurrecting a deleted treatment as a new document.
 *
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID (from the stored
 *   document, never from form state)
 * @param {Object} updates - Candidate field values; anything outside
 *   EDITABLE_TREATMENT_FIELDS is dropped
 * @returns {Promise<Object>} The sanitized payload that was written (empty
 *   object when there was nothing to write — no Firestore call is made then)
 * @throws {Error} When `t` is missing/unusable, or when the update fails
 *   (including "document does not exist")
 */
export async function updateTreatmentPlan(userID, t, updates) {
	if (!isUsableDocumentId(t)) {
		throw new Error("Invalid treatment id");
	}

	const source = updates && typeof updates === "object" ? updates : {};
	const sanitized = {};
	EDITABLE_TREATMENT_FIELDS.forEach((field) => {
		if (source[field] !== undefined) {
			sanitized[field] = source[field];
		}
	});

	// Nothing editable was supplied — skip the round trip rather than issuing
	// an empty update.
	if (Object.keys(sanitized).length === 0) {
		return sanitized;
	}

	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Treatments")
			.doc(String(t))
			.update(sanitized);

		invalidateTreatmentCache(userID);

		return sanitized;
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Updates an existing treatment with a full document merge.
 *
 * @deprecated use updateTreatmentPlan — this function writes whatever the
 * caller passes, so a stale snapshot overwrites `TreatmentCount`/`PhoneHistory`
 * and `set(..., { merge: true })` recreates deleted documents. Kept for legacy
 * callers only.
 * @param {string} userID - The unique identifier for the user
 * @param {Object} document - Treatment data
 * @param {number} document.t - Timestamp used as document ID
 * @returns {Promise<void>}
 */
export async function editTreatment(userID, document) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Treatments")
			.doc(String(document.t))
			.set(document, { merge: true })
			.catch((e) => {
				throw e;
			});

		invalidateTreatmentCache(userID);
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Deletes a treatment
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @returns {Promise<void>}
 */
export async function deleteTreatment(userID, t) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Treatments")
			.doc(String(t))
			.delete()
			.catch((e) => {
				throw e;
			});

		invalidateTreatmentCache(userID);
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Creates a new treatment
 * @param {string} userID - The unique identifier for the user
 * @param {Object} treatment - Treatment data
 * @param {boolean} treatment.Archived - Whether the treatment is archived
 * @param {string} treatment.ID - Animal ID
 * @param {number} treatment.t - Timestamp used as document ID
 * @param {string} treatment.Diagnose - Diagnosis
 * @param {string} treatment.Medicine - Medicine name
 * @param {number|string} treatment.TreatmentAmount - Treatment amount
 * @param {number|string} treatment.TreatmentCount - Treatment count
 * @param {string} treatment.TreatmentInterval - Treatment interval
 * @param {string} treatment.Comment - Additional comments
 * @returns {Promise<boolean>} Success status
 */
export async function setNewTreatment(userID, treatment) {
	if (!treatment || !treatment.t || !treatment.ID) {
		throw new Error("Invalid treatment data");
	}

	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Treatments")
			.doc(String(treatment.t))
			.set(
				{
					Archived: treatment.Archived || false,
					ID: treatment.ID,
					Mac: treatment.Mac || treatment.MAC || "",
					CustomName: treatment.CustomName || "",
					Group: treatment.Group || "",
					t: treatment.t,
					Diagnose: treatment.Diagnose || "",
					Medicine: treatment.Medicine || "",
					TreatmentAmount: parseInt(treatment.TreatmentAmount || 1, 10),
					TreatmentCount: parseInt(treatment.TreatmentCount || 0, 10),
					TreatmentInterval: parseInt(treatment.TreatmentInterval || 1, 10),
					Comment: treatment.Comment || "",
					PhoneHistory: Array.isArray(treatment.PhoneHistory)
						? treatment.PhoneHistory
						: [],
					...(treatment.Dosis ? { Dosis: treatment.Dosis } : {}),
					...(treatment.StartDate ? { StartDate: treatment.StartDate } : {}),
					...(treatment.StartDateSeconds
						? { StartDateSeconds: treatment.StartDateSeconds }
						: {}),
					...(typeof treatment.Validated === "boolean"
						? { Validated: treatment.Validated }
						: {}),
				},
				{ merge: true }
			)
			.catch((e) => {
				throw e;
			});

		invalidateTreatmentCache(userID);

		return true;
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Records one administered application for a treatment (web equivalent of the
 * mobile app's dose logging). Runs in a transaction so concurrent updates
 * cannot push TreatmentCount past TreatmentAmount.
 *
 * The history entry mirrors the mobile PhoneHistory convention (`t` in ms,
 * see calculateNextDoseDate in chatToolsService) and also updates
 * LastTreatmentDate, which downstream schedule logic uses as anchor.
 *
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @param {Object} [options]
 * @param {string} [options.dosage] - Administered dosage (free text, e.g. "10ml")
 * @param {string} [options.recordedBy] - UID of the acting user
 * @returns {Promise<Object>} The updated treatment document data
 */
export async function recordTreatmentApplication(userID, t, options = {}) {
	const docRef = db
		.collection("Users")
		.doc(userID)
		.collection("Treatments")
		.doc(String(t));

	try {
		const updated = await db.runTransaction(async (transaction) => {
			const snapshot = await transaction.get(docRef);
			if (!snapshot.exists) {
				throw new Error("Treatment not found");
			}

			const data = snapshot.data() || {};
			const amount = parseInt(data.TreatmentAmount || 1, 10);
			const count = parseInt(data.TreatmentCount || 0, 10);
			if (data.Done === true || (Number.isFinite(amount) && count >= amount)) {
				throw new Error("Treatment already completed");
			}

			const nowMs = Date.now();
			const historyEntry = {
				t: nowMs,
				timestamp: nowMs,
				source: "web",
				...(options.recordedBy ? { userId: options.recordedBy } : {}),
				...(options.dosage ? { Dosage: options.dosage } : {}),
			};
			const history = Array.isArray(data.PhoneHistory) ? data.PhoneHistory : [];
			const update = {
				TreatmentCount: count + 1,
				PhoneHistory: [...history, historyEntry],
				LastTreatmentDate: nowMs,
			};

			transaction.update(docRef, update);
			return { ...data, ...update };
		});

		invalidateTreatmentCache(userID);
		return updated;
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Reverts the most recent application that was recorded via the web
 * (undo for recordTreatmentApplication). Applications logged by the mobile
 * app are never touched.
 *
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @returns {Promise<Object>} The updated treatment document data
 */
export async function undoTreatmentApplication(userID, t) {
	const docRef = db
		.collection("Users")
		.doc(userID)
		.collection("Treatments")
		.doc(String(t));

	try {
		const updated = await db.runTransaction(async (transaction) => {
			const snapshot = await transaction.get(docRef);
			if (!snapshot.exists) {
				throw new Error("Treatment not found");
			}

			const data = snapshot.data() || {};
			const history = Array.isArray(data.PhoneHistory) ? data.PhoneHistory : [];
			const lastEntry = history.length > 0 ? history[history.length - 1] : null;
			if (!lastEntry || lastEntry.source !== "web") {
				throw new Error("No web application to undo");
			}

			const remainingHistory = history.slice(0, -1);
			const previousEntry =
				remainingHistory.length > 0
					? remainingHistory[remainingHistory.length - 1]
					: null;
			const count = parseInt(data.TreatmentCount || 0, 10);
			const update = {
				TreatmentCount: Math.max(count - 1, 0),
				PhoneHistory: remainingHistory,
				LastTreatmentDate:
					previousEntry?.t ??
					previousEntry?.timestamp ??
					firebase.firestore.FieldValue.delete(),
			};

			transaction.update(docRef, update);
			return {
				...data,
				...update,
				LastTreatmentDate: previousEntry?.t ?? previousEntry?.timestamp ?? null,
			};
		});

		invalidateTreatmentCache(userID);
		return updated;
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Explicitly closes a treatment regardless of remaining applications.
 *
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @returns {Promise<void>}
 */
export async function markTreatmentDone(userID, t) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Treatments")
			.doc(String(t))
			.set({ Done: true, DoneAt: Date.now() }, { merge: true });

		invalidateTreatmentCache(userID);
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Reopens a treatment that was closed via markTreatmentDone.
 *
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @returns {Promise<void>}
 */
export async function reopenTreatment(userID, t) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Treatments")
			.doc(String(t))
			.set(
				{ Done: false, DoneAt: firebase.firestore.FieldValue.delete() },
				{ merge: true }
			);

		invalidateTreatmentCache(userID);
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Creates a new birth document
 * @param {string} userID - The unique identifier for the user
 * @param {object} birthDocument - The birth document to create
 * @returns {Promise<string>} The ID of the newly created birth document
 */
export async function setNewBirthDocument(userID, birthDocument) {
  try {
    const docRef = await db
      .collection("Users")
      .doc(userID)
      .collection("BirthDocuments")
      .add({
        ...birthDocument,
        createdAt: new Date()
      });
    
    return docRef.id;
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Updates an existing birth document
 * @param {string} userID - The unique identifier for the user
 * @param {object} birthDocument - The birth document to update
 * @returns {Promise<void>}
 */
export async function editBirthDocument(userID, birthDocument) {
  try {
    await db
      .collection("Users")
      .doc(userID)
      .collection("BirthDocuments")
      .doc(birthDocument.id)
      .update(birthDocument);
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Deletes a birth document
 * @param {string} userID - The unique identifier for the user
 * @param {object} birthDocument - The birth document to delete
 * @returns {Promise<void>}
 */
export async function deleteBirthDocument(userID, birthDocument) {
  try {
    await db
      .collection("Users")
      .doc(userID)
      .collection("BirthDocuments")
      .doc(birthDocument.id)
      .delete();
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Gets all birth documents for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of birth document objects
 */
export async function getAllBirthDocuments(userID) {
  try {
    const birthDocuments = [];
    
    const snapshot = await db
      .collection("Users")
      .doc(userID)
      .collection("BirthDocuments")
      .get();
    
    snapshot.forEach((doc) => {
      birthDocuments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return birthDocuments;
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Gets birth documents for a specific animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} uid - The animal's unique identifier
 * @returns {Promise<Array>} Array of birth document objects for the animal
 */
export async function getBirthDocumentsOfAnimal(userID, uid) {
  try {
    const birthDocuments = [];
    
    const snapshot = await db
      .collection("Users")
      .doc(userID)
      .collection("BirthDocuments")
      .where("animalId", "==", uid)
      .get();
    
    snapshot.forEach((doc) => {
      birthDocuments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return birthDocuments;
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Creates a new insemination document
 * @param {string} userID - The unique identifier for the user
 * @param {object} inseminationDocument - The insemination document to create
 * @returns {Promise<string>} The ID of the newly created insemination document
 */
export async function setNewInseminationDocument(userID, inseminationDocument) {
  try {
    const docRef = await db
      .collection("Users")
      .doc(userID)
      .collection("InseminationDocuments")
      .add({
        ...inseminationDocument,
        createdAt: new Date()
      });
    
    return docRef.id;
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Updates an existing insemination document
 * @param {string} userID - The unique identifier for the user
 * @param {object} inseminationDocument - The insemination document to update
 * @returns {Promise<void>}
 */
export async function editInseminationDocument(userID, inseminationDocument) {
  try {
    await db
      .collection("Users")
      .doc(userID)
      .collection("InseminationDocuments")
      .doc(inseminationDocument.id)
      .update(inseminationDocument);
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Deletes an insemination document
 * @param {string} userID - The unique identifier for the user
 * @param {object} inseminationDocument - The insemination document to delete
 * @returns {Promise<void>}
 */
export async function deleteInseminationDocument(userID, inseminationDocument) {
  try {
    await db
      .collection("Users")
      .doc(userID)
      .collection("InseminationDocuments")
      .doc(inseminationDocument.id)
      .delete();
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Gets all insemination documents for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of insemination document objects
 */
export async function getAllInseminationDocuments(userID) {
  try {
    const inseminationDocuments = [];
    
    const snapshot = await db
      .collection("Users")
      .doc(userID)
      .collection("InseminationDocuments")
      .get();
    
    snapshot.forEach((doc) => {
      inseminationDocuments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return inseminationDocuments;
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Default diagnose categories for new treatments. Values are stable English
 * tokens (translated via utils/diagnosisLabels); calf-typical conditions
 * first. "Death" is intentionally not offered for new treatments — historical
 * documents with that value still translate correctly.
 */
export const DEFAULT_DIAGNOSE_CATEGORIES = [
	"Diarrhea",
	"Pneumonia",
	"NavelInflammation",
	"Lameness",
	"Sickness",
	"Other",
];

/**
 * Gets diagnose categories for treatments. Reads the farm-specific list from
 * Users/{uid}/Account/Medical (field `Diagnoses`), falling back to the
 * defaults when none are configured.
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<string>>} Array of diagnose category names
 */
export const getDiagnoseCategories = async (userId) => {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Medical")
			.get();

		const configured = snapshot.exists ? snapshot.data()?.Diagnoses : null;
		if (Array.isArray(configured)) {
			const cleaned = configured
				.filter((entry) => typeof entry === "string" && entry.trim() !== "")
				.map((entry) => entry.trim());
			if (cleaned.length > 0) {
				return cleaned;
			}
		}

		return [...DEFAULT_DIAGNOSE_CATEGORIES];
	} catch (error) {
		console.error("[treatmentService][getDiagnoseCategories] - Error getting diagnose categories:", error);
		return [...DEFAULT_DIAGNOSE_CATEGORIES];
	}
};

/**
 * Saves the farm-specific diagnose categories.
 * @param {string} userId - The unique identifier for the user
 * @param {Array<string>} categories - Diagnose category names
 * @returns {Promise<void>}
 */
export const saveDiagnoseCategories = async (userId, categories) => {
	const cleaned = (Array.isArray(categories) ? categories : [])
		.filter((entry) => typeof entry === "string" && entry.trim() !== "")
		.map((entry) => entry.trim());

	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Medical")
			.set({ Diagnoses: cleaned }, { merge: true });
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
};

/**
 * Gets medicines list for treatments from the canonical location
 * Users/{uid}/Account/Medical (field `Medications`) — the same document the
 * treatment settings dialog maintains.
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<string>>} Array of medicine names
 */
export const getMedicines = async (userId) => {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Medical")
			.get();

		const medications = snapshot.exists ? snapshot.data()?.Medications : null;
		if (Array.isArray(medications)) {
			return medications.filter(
				(entry) => typeof entry === "string" && entry.trim() !== ""
			);
		}

		return [];
	} catch (error) {
		console.error("[treatmentService][getMedicines] - Error getting medicines:", error);
		return [];
	}
};